import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import {
    IReviewsProductTypeID,
    WebPageURL,
    isoReviewsProductTypeID,
    isoWebPageURL,
} from "../../models/nominals";
import { IReviewQuery, IReviewQueryFacets } from "../../models/reviews";
import { parseFacetValues, serializeFacetValues } from "../../utils/reviews";

/**
 * The set of facet keys (excluding product_type_id, which maps to its own
 * product-types state slice) that map to the store's `facetValues`.
 *
 * `as const` preserves the literal element types so the exhaustiveness guard
 * below can compare them against `IReviewQueryFacets`.
 */
const FACET_VALUE_KEYS = [
    "text",
    "rating",
    "product_uuid",
    "product_id",
    "source_id",
    "product_variant_id",
] as const satisfies readonly Exclude<IReviewQueryFacets, "product_type_id">[];

/**
 * Compile-time exhaustiveness guard: if a facet key (other than
 * product_type_id) is added to `IReviewQueryFacets` without also being added to
 * `FACET_VALUE_KEYS`, this assertion fails to compile — preventing the new
 * facet from being silently ignored during URL hydration.
 */
type _MissingFacetValueKeys = Exclude<
    Exclude<IReviewQueryFacets, "product_type_id">,
    (typeof FACET_VALUE_KEYS)[number]
>;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _facetValueKeysAreExhaustive: [_MissingFacetValueKeys] extends [never]
    ? true
    : ["FACET_VALUE_KEYS is missing facet keys:", _MissingFacetValueKeys] =
    true;

interface IParams {
    [name: string]: string | number | number[] | undefined | null;
}

type IFacetParams = {
    [T in IReviewQueryFacets]: string | number | null;
};

export const getParamsFromFacetValues = (
    facetValues: IReviewQuery,
    productTypeIDs: readonly IReviewsProductTypeID[],
): IFacetParams => {
    const params: IFacetParams = {
        text: null,
        rating: null,
        product_uuid: null,
        product_id: null,
        source_id: null,
        product_variant_id: null,
        ...facetValues,
        product_type_id: serializeFacetValues(productTypeIDs),
    };
    return params;
};

export const buildReviewsURL = (
    baseURL: WebPageURL,
    params: IParams,
): string => {
    const unwrappedBaseURL = isoWebPageURL.unwrap(baseURL);
    const combinedParams = new URLSearchParams(
        unwrappedBaseURL.split("?").splice(1).join("?"),
    );
    // Build the new, combined query params object
    Object.keys(params).forEach((key) => {
        const value = params[key];
        if (value === undefined || value === null) {
            combinedParams.delete(key);
        } else {
            combinedParams.set(key, `${value}`);
        }
    });
    return `${unwrappedBaseURL.split("?")[0]}?${combinedParams.toString()}`;
};

export const updateQueryParams = (params: IParams): void => {
    // Build the URL and replace the current history state with it
    const window = getWindow();
    if (!window) {
        return;
    }
    const url = buildReviewsURL(
        isoWebPageURL.wrap(window.location.href),
        params,
    );
    window.history.replaceState(null, "", url.toString());
};

export const readQueryParams = (): IParams => {
    const window = getWindow();
    if (!window) {
        return {};
    }
    const urlObj = new URL(window.location.href);
    const query: IParams = {};
    urlObj.searchParams.forEach((value, name) => {
        query[name] = `${value}`;
    });
    if (query.product_type_id) {
        const typeIDs = parseFacetValues(`${query.product_type_id || ""}`).map(
            (id: string) => parseInt(id, 10),
        );
        query.product_type_id = typeIDs;
    }
    return query;
};

/**
 * Derive the reviews filter state (product types + facet values) from the
 * current URL query string.
 *
 * In deployed environments a CDN strips non-allowlisted query params before
 * the request reaches Django, so the SSR-rendered reviews are UNFILTERED even
 * though the browser URL still carries the filter params. On client hydration
 * we re-read the URL and apply these filters so the viewer fetches the
 * filtered reviews the user actually requested.
 *
 * Mirrors the server's `if value:` behavior — empty-string params (e.g.
 * `product_id=`) are skipped so they don't create empty filters.
 */
export const getInitialFilterStateFromURL = (): {
    productTypeIDs: readonly IReviewsProductTypeID[];
    facetValues: IReviewQuery;
} => {
    const query = readQueryParams();

    // product_type_id is parsed to number[] by readQueryParams; map to the
    // product-types state slice.
    const rawProductTypeIDs = query.product_type_id;
    const productTypeIDs: readonly IReviewsProductTypeID[] = Array.isArray(
        rawProductTypeIDs,
    )
        ? rawProductTypeIDs.map((id) => isoReviewsProductTypeID.wrap(id))
        : [];

    // All other facet keys map to facetValues; skip empty-string values.
    const facetValues: IReviewQuery = {};
    for (const key of FACET_VALUE_KEYS) {
        const value = query[key];
        if (
            value !== undefined &&
            value !== null &&
            !Array.isArray(value) &&
            `${value}` !== ""
        ) {
            facetValues[key] = `${value}`;
        }
    }

    return { productTypeIDs, facetValues };
};
